import React from 'react'
import type { Booking } from 'repositories/BerthApi/types/booking'
import type { BookingMasterData } from 'repositories/BerthApi/getBookingMasterData/type'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'
import { BOOKING_STATUS } from 'constants/Booking'
import { useCallingAction } from 'containers/SpBerthManagement/hooks/useCallingAction'
import { getStatusLabel } from 'containers/SpBerthManagement/hooks/useBookingStatusChange'

import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { useModalScroll } from '../../hooks/useModalScroll'
import type { Berth, BerthCard } from '../../types'
import { BookingDetailCard } from '../BookingDetailCard'
import { CallingDialog } from '../CallingDialog'
import { Dialog } from '../Dialog'

import styles from './index.module.css'

type Props = {
  berthId: string
  berths: Berth[]
  date: string
  cards: BerthCard[]
  bookingsMap: Record<string, Booking>
  masterData: BookingMasterData | null
  tenantId: string | null
  selectedCardId: string | null
  canUnmannedCheckin: boolean
  onClose: () => void
  onNotify: (payload: SnackbarPayload) => void
  onEditClick?: (bookingKey: string) => void
}

/**
 * 呼び出しボタンを表示すべきかどうかを判定する
 * - 受付済 (Recept) のみボタンを表示
 * - 無人チェックイン (canUnmannedCheckin) 有効時は非表示
 * - 総合予約の場合は先頭の受付済 (waiting_status が null or 0) のみ
 */
const shouldShowCallingAction = (booking: Booking, canUnmannedCheckin: boolean): boolean => {
  if (booking.status !== BOOKING_STATUS.Recept) return false
  if (canUnmannedCheckin) return false
  if (booking.generalBookingId == null) return true
  return booking.generalBookingWaitingStatus == null || booking.generalBookingWaitingStatus === 0
}

export const UnassignedDetailModal: React.FC<Props> = ({
  berthId,
  berths,
  date,
  cards,
  bookingsMap,
  masterData,
  tenantId,
  selectedCardId,
  canUnmannedCheckin,
  onClose,
  onNotify,
  onEditClick,
}) => {
  const isOpen = selectedCardId !== null
  const { focusedCardId, cardRefs, scrollAreaRef, handleCardClick } = useModalScroll(
    selectedCardId,
    isOpen
  )

  const {
    callingTarget,
    forceFinish,
    isDialogLoading,
    handleCallingStart,
    handleCallingOk,
    handleCallingCancel,
    handleForceFinishOk,
    handleForceFinishCancel,
  } = useCallingAction({ date, tenantId, onNotify })

  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <>
      <div className={styles.overlay} onClick={handleOverlayClick}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h3 className={styles.title}>バース未割り当て予約一覧</h3>
            <button className={styles.closeIconButton} onClick={onClose} aria-label="閉じる">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>

          <div className={styles.scrollArea} ref={scrollAreaRef}>
            {cards.map((card) => {
              const booking = bookingsMap[card.id]
              if (!booking) return null

              const showCalling = shouldShowCallingAction(booking, canUnmannedCheckin)

              return (
                <BookingDetailCard
                  key={card.id}
                  booking={booking}
                  bookingKey={card.id}
                  masterData={masterData}
                  highlighted={card.id === focusedCardId}
                  hideStatusAction={!showCalling}
                  callingAction={
                    showCalling
                      ? {
                          label: '呼出中にする',
                          onClick: () => handleCallingStart(booking),
                        }
                      : undefined
                  }
                  onCardClick={() => handleCardClick(card.id)}
                  onEditClick={onEditClick}
                  cardRef={(el) => {
                    cardRefs.current[card.id] = el
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>

      {callingTarget && (
        <CallingDialog
          berths={berths}
          defaultBerthId={berthId}
          driverName={callingTarget.booking.driverName ?? null}
          driverPhone={callingTarget.booking.driverPhone ?? null}
          onOk={handleCallingOk}
          onCancel={handleCallingCancel}
          isLoading={isDialogLoading}
        />
      )}

      {forceFinish && (
        <Dialog
          onClose={handleForceFinishCancel}
          prevButtonProps={{
            label: 'キャンセル',
            onClick: handleForceFinishCancel,
            variant: 'cancel',
            disabled: isDialogLoading,
          }}
          nextButtonProps={{
            label: 'OK',
            onClick: handleForceFinishOk,
            variant: 'primary',
            disabled: isDialogLoading,
          }}
        >
          <p className={styles.dialogMessage}>
            以下の予約がまだ{getStatusLabel(forceFinish.previousBooking.status ?? null)}ですが、
            <br />
            完了にしてよいですか？
          </p>
          <BookingDetailCard
            booking={forceFinish.previousBooking}
            masterData={masterData}
            hideEditAction
            hideStatusAction
          />
        </Dialog>
      )}
    </>
  )
}
